import React from 'react';

import { asset } from '../../../../lib/asset';
import { PreviewPlaceholder } from '../../../../components/wizard/PreviewPlaceholder';
import {
  MacRoot,
  MacScreen,
  MacTopBar,
  FrameContent,
  FrameOverlayImg,
  PhoneRoot,
  PhoneScreen,
  PhoneTopBar,
  PhoneBottomBar,
} from '../../../../components/preview-stage/PreviewStage.styles';
import { PHONE_FRAME_DEFAULT, PHONE_RADIUS_RATIO, type DeviceFrameArt } from './frame-art';
import { ScreenshotImg } from './StaticDevices.styles';

export type StaticMacDeviceProps = {
  /** Static screenshot URL (empty while the selection hasn't resolved yet). */
  imageSrc: string;
  /** The image 404'd: let the caller retry a fallback URL (a different language
   *  capture). Reveal stays gated on a subsequent successful `onLoad`. */
  onImageError: () => void;
  topColor?: string;
  bgColor?: string;
  frame: DeviceFrameArt;
  /** Run the loading spinner over the placeholder. Default true (a capture is loading);
   *  pass false for the SETTLED empty state (no screenshot exists for the selection, e.g.
   *  the tablet image-only path) so the device shows a flat brand fill instead of
   *  spinning forever on a preview that will never arrive. */
  spinner?: boolean;
};

/**
 * Finish-step desktop device: the same MacBook bezel as the live preview, but its
 * screen fills with a pre-rendered screenshot instead of an iframe. No reveal-gate
 * plumbing to share with a sibling iframe, this device owns its own `shown` state
 * off the image's own `load`.
 */
export const StaticMacDevice = ({
  imageSrc,
  onImageError,
  topColor,
  bgColor,
  frame,
  spinner = true,
}: StaticMacDeviceProps): React.ReactElement => {
  // Keyed on the src that loaded, not reset in an effect (see MobileInlinePreview): a
  // fast load can land before a deferred effect flush, which would hide it for good.
  const [loadedSrc, setLoadedSrc] = React.useState('');
  const shown = imageSrc !== '' && loadedSrc === imageSrc;

  return (
    <MacRoot>
      <MacScreen
        $bg={topColor}
        $left={frame.screen.left}
        $top={frame.screen.top}
        $width={frame.screen.width}
        $height={frame.screen.height}
        $bare={false}
      >
        <MacTopBar $color={topColor} $bare={false} />
        <FrameContent>
          {imageSrc ? (
            <ScreenshotImg
              src={imageSrc}
              alt=""
              $shown={shown}
              $fit="cover"
              onLoad={() => setLoadedSrc(imageSrc)}
              onError={onImageError}
            />
          ) : null}
        </FrameContent>
        <PreviewPlaceholder shown={!shown} bgColor={bgColor} spinner={spinner} />
      </MacScreen>
      <FrameOverlayImg src={asset(frame.src)} alt="" $hidden={false} />
    </MacRoot>
  );
};

export type StaticPhoneDeviceProps = {
  imageSrc: string;
  /** The image 404'd: let the caller retry a fallback URL (a different language
   *  capture). Reveal stays gated on a subsequent successful `onLoad`. */
  onImageError: () => void;
  topColor?: string;
  bottomColor?: string;
  bgColor?: string;
  /** Run the loading spinner over the placeholder. Default true; pass false for the
   *  settled empty state (no screenshot exists) so it doesn't spin on a preview that
   *  will never arrive (the tablet image-only path). */
  spinner?: boolean;
};

/** Finish-step mobile device: the same iPhone bezel as the live preview, screen filled
 *  with the same static screenshot (no separate mobile capture exists). */
export const StaticPhoneDevice = ({
  imageSrc,
  onImageError,
  topColor,
  bottomColor,
  bgColor,
  spinner = true,
}: StaticPhoneDeviceProps): React.ReactElement => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [radius, setRadius] = React.useState(42);
  // Keyed on the src that loaded, not reset in an effect: see StaticMacDevice.
  const [loadedSrc, setLoadedSrc] = React.useState('');
  const shown = imageSrc !== '' && loadedSrc === imageSrc;

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const measure = (): void => setRadius(root.offsetWidth * PHONE_RADIUS_RATIO);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, []);

  return (
    <PhoneRoot ref={rootRef}>
      <PhoneScreen
        $radius={radius}
        $bg={topColor}
        $bare={false}
        $left={PHONE_FRAME_DEFAULT.screen.left}
        $top={PHONE_FRAME_DEFAULT.screen.top}
        $width={PHONE_FRAME_DEFAULT.screen.width}
        $height={PHONE_FRAME_DEFAULT.screen.height}
      >
        <PhoneTopBar $color={topColor} $bare={false} />
        <FrameContent>
          {imageSrc ? (
            <ScreenshotImg
              src={imageSrc}
              alt=""
              $shown={shown}
              $fit="fill"
              onLoad={() => setLoadedSrc(imageSrc)}
              onError={onImageError}
            />
          ) : null}
        </FrameContent>
        <PhoneBottomBar $color={shown ? bottomColor : topColor} $bare={false} />
        <PreviewPlaceholder shown={!shown} bgColor={bgColor} spinner={spinner} />
      </PhoneScreen>
      <FrameOverlayImg src={asset(PHONE_FRAME_DEFAULT.src)} alt="" $hidden={false} />
    </PhoneRoot>
  );
};
